(() => {
  'use strict';

  const sectionSelector = '[data-fit-data-url]';
  const controllers = new Map();
  const dataCache = new Map();
  const inflightRequests = new Map();

  const querySections = (scope = document) => {
    if (!scope) return [];

    return scope.matches?.(sectionSelector)
      ? [scope]
      : Array.from(scope.querySelectorAll(sectionSelector));
  };

  const fetchFitData = async (dataUrl) => {
    if (!dataUrl) {
      throw new Error('Missing data URL.');
    }

    if (dataCache.has(dataUrl)) {
      return dataCache.get(dataUrl);
    }

    if (inflightRequests.has(dataUrl)) {
      return inflightRequests.get(dataUrl);
    }

    const request = fetch(dataUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch JSON: ${response.status}`);
        }

        return response.json();
      })
      .then((data) => {
        dataCache.set(dataUrl, data);
        inflightRequests.delete(dataUrl);
        return data;
      })
      .catch((error) => {
        inflightRequests.delete(dataUrl);
        throw error;
      });

    inflightRequests.set(dataUrl, request);
    return request;
  };

  class ChildseatFitChecker {
    constructor(section) {
      this.section = section;
      this.dataUrl = section.dataset.fitDataUrl || '';
      this.selectPlaceholder = section.dataset.fitSelectPlaceholder || '選択してください';
      this.resultsSummarySuffix = section.dataset.fitResultsSummarySuffix || 'の結果を表示しています。';
      this.unknownYearLabel = section.dataset.fitUnknownYearLabel || '年式不明';

      this.form = section.querySelector('[data-fit-form]');
      this.makerSelect = section.querySelector('[data-fit-maker]');
      this.carSelect = section.querySelector('[data-fit-initial]');
      this.yearSelect = section.querySelector('[data-fit-car-name]');
      this.submitButton = section.querySelector('[data-fit-submit]');
      this.resetButton = section.querySelector('[data-fit-reset]');

      this.resultsWrap = section.querySelector('[data-fit-results]');
      this.resultsSummary = section.querySelector('[data-fit-results-summary]');
      this.emptyState = section.querySelector('[data-fit-empty]');
      this.errorState = section.querySelector('[data-fit-error]');

      this.groups = {
        ok: {
          wrapper: section.querySelector('[data-fit-group="ok"]'),
          container: section.querySelector('[data-fit-cards-ok]')
        },
        conditional: {
          wrapper: section.querySelector('[data-fit-group="conditional"]'),
          container: section.querySelector('[data-fit-cards-conditional]')
        },
        ng: {
          wrapper: section.querySelector('[data-fit-group="ng"]'),
          container: section.querySelector('[data-fit-cards-ng]')
        },
        unchecked: {
          wrapper: section.querySelector('[data-fit-group="unchecked"]'),
          container: section.querySelector('[data-fit-cards-unchecked]')
        }
      };

      this.cardTemplate = section.querySelector('[data-fit-card-template]');
      this.productMapScript = section.querySelector('[data-fit-product-map]');
      this.productMap = {};

      this.data = null;
      this.loadingPromise = null;
      this.prefetchObserver = null;
      this.cleanupFns = [];
      this.isDestroyed = false;

      this.currentMakerId = '';
      this.currentCarId = '';
      this.currentSpecId = '';
    }

    addListener(target, eventName, handler, options) {
      if (!target) return;

      target.addEventListener(eventName, handler, options);
      this.cleanupFns.push(() => target.removeEventListener(eventName, handler, options));
    }

    init() {
      if (!this.form || !this.makerSelect || !this.carSelect || !this.yearSelect) {
        return;
      }

      this.parseProductMap();
      this.bindEvents();
      this.prepareInitialState();
      this.setupDeferredLoading();

      if (dataCache.has(this.dataUrl) || inflightRequests.has(this.dataUrl)) {
        this.ensureDataLoaded().catch(() => {});
      }
    }

    prepareInitialState() {
      this.replaceSelectOptions(this.makerSelect, []);
      this.resetCars();
      this.resetYears();
      this.hideResults();
      this.hideError();
      this.hideEmpty();
      this.updateSubmitState();
    }

    bindEvents() {
      this.addListener(this.makerSelect, 'change', async () => {
        await this.ensureDataLoaded();

        this.currentMakerId = this.makerSelect.value;
        this.currentCarId = '';
        this.currentSpecId = '';

        this.populateCars();
        this.resetYears();
        this.updateSubmitState();
        this.hideResults();
      });

      this.addListener(this.carSelect, 'change', async () => {
        await this.ensureDataLoaded();

        this.currentCarId = this.carSelect.value;
        this.currentSpecId = '';

        this.populateYears();
        this.updateSubmitState();
        this.hideResults();
      });

      this.addListener(this.yearSelect, 'change', async () => {
        await this.ensureDataLoaded();

        this.currentSpecId = this.yearSelect.value;
        this.updateSubmitState();
        this.hideResults();
      });

      this.addListener(this.form, 'submit', (event) => {
        event.preventDefault();
        this.handleSearch();
      });

      if (this.resetButton) {
        this.addListener(this.resetButton, 'click', () => {
          this.resetAll();
        });
      }
    }

    setupDeferredLoading() {
      const prefetch = () => {
        this.ensureDataLoaded().catch((error) => {
          console.error('[ChildseatFitChecker] Failed to initialize:', error);
          this.showError();
        });
      };

      this.addListener(this.section, 'pointerenter', prefetch, { passive: true });
      this.addListener(this.section, 'focusin', prefetch);
      this.addListener(this.section, 'touchstart', prefetch, { passive: true });

      if ('IntersectionObserver' in window) {
        this.prefetchObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            prefetch();
            this.prefetchObserver?.disconnect();
            this.prefetchObserver = null;
          },
          { rootMargin: '300px 0px' }
        );

        this.prefetchObserver.observe(this.section);
      } else {
        const timeoutId = window.setTimeout(prefetch, 2000);
        this.cleanupFns.push(() => window.clearTimeout(timeoutId));
      }

      this.cleanupFns.push(() => {
        this.prefetchObserver?.disconnect();
        this.prefetchObserver = null;
      });
    }

    async ensureDataLoaded() {
      if (this.data) return this.data;
      if (this.loadingPromise) return this.loadingPromise;

      this.hideError();
      this.setLoadingState(true);

      this.loadingPromise = fetchFitData(this.dataUrl)
        .then((data) => {
          if (this.isDestroyed) return data;

          this.data = data;
          this.populateMakers();
          return data;
        })
        .catch((error) => {
          if (!this.isDestroyed) {
            this.showError();
          }

          throw error;
        })
        .finally(() => {
          if (!this.isDestroyed) {
            this.setLoadingState(false);
            this.updateSubmitState();
          }

          this.loadingPromise = null;
        });

      return this.loadingPromise;
    }

    setLoadingState(isLoading) {
      this.section.classList.toggle('is-loading', isLoading);
      this.section.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    parseProductMap() {
      if (!this.productMapScript) {
        this.productMap = {};
        return;
      }

      try {
        const raw = this.productMapScript.textContent.trim() || '{}';
        this.productMap = JSON.parse(raw);
      } catch (error) {
        console.error('[ChildseatFitChecker] Failed to parse product map:', error);
        this.productMap = {};
      }
    }

    populateMakers() {
      const makers = Array.isArray(this.data?.makers) ? [...this.data.makers] : [];

      makers.sort((a, b) => {
        const orderA = typeof a.sort_order === 'number' ? a.sort_order : 9999;
        const orderB = typeof b.sort_order === 'number' ? b.sort_order : 9999;
        return orderA - orderB;
      });

      this.replaceSelectOptions(
        this.makerSelect,
        makers.map((maker) => ({
          value: maker.id,
          label: maker.name
        }))
      );

      this.makerSelect.disabled = makers.length === 0;
      this.resetCars();
      this.resetYears();
      this.updateSubmitState();
    }

    populateCars() {
      if (!this.currentMakerId) {
        this.resetCars();
        return;
      }

      const cars = this.getCarsByMaker(this.currentMakerId).sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '', 'ja');
      });

      this.replaceSelectOptions(
        this.carSelect,
        cars.map((car) => ({
          value: car.id,
          label: car.name
        }))
      );

      this.carSelect.disabled = cars.length === 0;
    }

    populateYears() {
      if (!this.currentCarId) {
        this.resetYears();
        return;
      }

      const specs = this.getSpecsByCarId(this.currentCarId);

      this.replaceSelectOptions(
        this.yearSelect,
        specs.map((spec) => ({
          value: spec.id,
          label: this.getSpecLabel(spec)
        }))
      );

      this.yearSelect.disabled = specs.length === 0;
    }

    handleSearch() {
      if (!this.currentSpecId) {
        return;
      }

      const selectedSpec = this.getSpecById(this.currentSpecId);

      if (!selectedSpec) {
        this.showEmpty();
        return;
      }

      const fits = this.getFitsBySpecId(selectedSpec.id);

      if (!fits.length) {
        this.showEmpty();
        return;
      }

      const grouped = {
        ok: [],
        conditional: [],
        ng: [],
        unchecked: []
      };

      fits.forEach((fit) => {
        const status = fit.status || 'unchecked';
        if (!grouped[status]) {
          grouped.unchecked.push(fit);
        } else {
          grouped[status].push(fit);
        }
      });

      this.renderResults(selectedSpec, grouped);
    }

    renderResults(selectedSpec, groupedFits) {
      this.clearAllGroups();
      this.hideError();
      this.hideEmpty();

      const visibleGroupCount = Object.values(groupedFits).filter((items) => items.length > 0).length;
      if (visibleGroupCount === 0) {
        this.showEmpty();
        return;
      }

      Object.entries(groupedFits).forEach(([status, fits]) => {
        if (!fits.length) return;

        const group = this.groups[status];
        if (!group || !group.container || !group.wrapper) return;

        fits.forEach((fit) => {
          const card = this.createCard(fit, status);
          if (card) {
            group.container.appendChild(card);
          }
        });

        group.wrapper.hidden = false;
      });

      const car = this.getCarById(selectedSpec.car_id);
      const maker = car ? this.getMakerById(car.maker_id) : null;

      let summaryText = '';

      if (maker && car) {
        summaryText += `${maker.name} ${car.name}`;
      } else if (car) {
        summaryText += car.name;
      }

      const specLabel = this.getSpecLabel(selectedSpec);
      if (specLabel) {
        summaryText += ` / ${specLabel}`;
      }

      if (summaryText) {
        summaryText += ` ${this.resultsSummarySuffix}`;
      }

      if (this.resultsSummary) {
        this.resultsSummary.textContent = summaryText;
      }

      if (this.resultsWrap) {
        this.resultsWrap.hidden = false;

        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        this.resultsWrap.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start'
        });
      }
    }

    createCard(fit, status) {
      if (!this.cardTemplate) return null;

      const fragment = this.cardTemplate.content.cloneNode(true);
      const article = fragment.querySelector('.fit-result-card');
      if (!article) return null;

      const product = this.getProductById(fit.product_id);
      if (!product) return null;

      const productDisplay = this.getProductDisplayData(product);

      const badge = fragment.querySelector('[data-fit-card-badge]');
      const method = fragment.querySelector('[data-fit-card-method]');
      const title = fragment.querySelector('[data-fit-card-title]');
      const notes = fragment.querySelector('[data-fit-card-notes]');
      const link = fragment.querySelector('[data-fit-card-link]');
      const media = fragment.querySelector('[data-fit-card-media]');

      if (badge) {
        badge.textContent = this.getStatusLabel(status);
        badge.classList.add(`is-${status}`);
      }

      if (method) {
        method.textContent = this.getInstallMethodLabel(product.install_type);
      }

      if (title) {
        title.textContent = productDisplay?.title || product.name || '';
      }

      if (notes) {
        const noteItems = this.buildNoteList(product, fit, selectedSpecNoteArraySafe(this.data, fit.spec_id));
        if (noteItems.length) {
          const ul = document.createElement('ul');
          ul.className = 'fit-result-card__note-list';

          noteItems.forEach((item) => {
            const li = document.createElement('li');
            li.textContent = item;
            ul.appendChild(li);
          });

          notes.appendChild(ul);
        }
      }

      if (link) {
        link.href = productDisplay?.url || this.buildProductUrl(product.product_handle);
      }

      if (media) {
        media.innerHTML = '';
        media.classList.remove('is-placeholder');

        if (productDisplay?.image) {
          const img = document.createElement('img');
          img.src = productDisplay.image;
          img.alt = productDisplay.title || product.name || '';
          img.loading = 'lazy';
          img.className = 'fit-result-card__image';
          media.appendChild(img);
        } else {
          media.classList.add('is-placeholder');

          const placeholder = document.createElement('div');
          placeholder.className = 'fit-result-card__placeholder';
          placeholder.textContent = productDisplay?.title || product.name || '';
          media.appendChild(placeholder);
        }
      }

      article.dataset.status = status;
      article.dataset.productId = product.id;

      return fragment;
    }

    getProductDisplayData(product) {
      if (!product) return null;

      const handle = product.product_handle || '';
      const shopifyProduct = this.productMap[handle] || {};

      return {
        handle,
        title: shopifyProduct.title || product.name || '',
        url: shopifyProduct.url || this.buildProductUrl(handle),
        image: shopifyProduct.image || '',
        name: product.name || ''
      };
    }

    buildNoteList(product, fit, specNotes) {
      const items = [];

      const mergedCodes = [
        ...(Array.isArray(product.default_caution_codes) ? product.default_caution_codes : []),
        ...(Array.isArray(fit.caution_codes) ? fit.caution_codes : [])
      ];

      const uniqueCodes = [...new Set(mergedCodes)];

      if (fit.note) {
        items.push(fit.note);
      }

      if (Array.isArray(specNotes) && specNotes.length) {
        specNotes.forEach((note) => {
          const text = this.resolveNoteToken(note);
          if (text) items.push(text);
        });
      }

      uniqueCodes.forEach((code) => {
        const text = this.resolveNoteToken(code);
        if (text) {
          items.push(text);
        }
      });

      return [...new Set(items)];
    }

    resolveNoteToken(token) {
      if (!token) return '';

      const cautionCodeMap = this.data?.caution_codes || {};
      if (cautionCodeMap[token]) {
        return cautionCodeMap[token];
      }

      const legacyNoteMap = {
        captain_seat: 'キャプテンシート車は座席形状をご確認ください。',
        seat_shape_check: '座面形状により取付条件が異なる場合があります。'
      };

      return legacyNoteMap[token] || token;
    }

    getCarsByMaker(makerId) {
      return (this.data?.cars || []).filter((car) => {
        return car.maker_id === makerId && car.active !== false;
      });
    }

    getCarById(carId) {
      return (this.data?.cars || []).find((car) => car.id === carId) || null;
    }

    getMakerById(makerId) {
      return (this.data?.makers || []).find((maker) => maker.id === makerId) || null;
    }

    getSpecsByCarId(carId) {
      return (this.data?.specs || [])
        .filter((spec) => spec.car_id === carId)
        .sort((a, b) => {
          const yearToA = typeof a.year_to === 'number' ? a.year_to : 0;
          const yearToB = typeof b.year_to === 'number' ? b.year_to : 0;
          const yearFromA = typeof a.year_from === 'number' ? a.year_from : 0;
          const yearFromB = typeof b.year_from === 'number' ? b.year_from : 0;

          if (yearToA !== yearToB) return yearToB - yearToA;
          if (yearFromA !== yearFromB) return yearFromB - yearFromA;
          return (b.label || '').localeCompare(a.label || '', 'ja');
        });
    }

    getSpecById(specId) {
      return (this.data?.specs || []).find((spec) => spec.id === specId) || null;
    }

    getFitsBySpecId(specId) {
      return (this.data?.fits || [])
        .filter((fit) => fit.spec_id === specId)
        .sort((a, b) => this.getStatusOrder(a.status) - this.getStatusOrder(b.status));
    }

    getProductById(productId) {
      return (this.data?.products || []).find((product) => product.id === productId) || null;
    }

    getSpecLabel(spec) {
      if (!spec) return '';

      if (spec.label) {
        return spec.label;
      }

      const from = typeof spec.year_from === 'number' ? spec.year_from : null;
      const to = typeof spec.year_to === 'number' ? spec.year_to : null;

      if (from && to) {
        if (to >= 2099) return `${from}年以降`;
        if (from === to) return `${from}年`;
        return `${from}年〜${to}年`;
      }

      if (from) return `${from}年以降`;
      if (to) return `${to}年まで`;

      return this.unknownYearLabel;
    }

    getStatusOrder(status) {
      const order = {
        ok: 1,
        conditional: 2,
        unchecked: 3,
        ng: 4
      };
      return order[status] || 99;
    }

    getStatusLabel(status) {
      const labels = {
        ok: '取付可能',
        conditional: '条件付き',
        ng: '取付不可',
        unchecked: '要確認'
      };
      return labels[status] || '要確認';
    }

    getInstallMethodLabel(type) {
      const labels = {
        isofix_support_leg: 'ISOFIX + サポートレッグ',
        isofix_booster: 'ISOFIX / 3点式シートベルト',
        seatbelt_3point: '3点式シートベルト固定'
      };
      return labels[type] || '';
    }

    buildProductUrl(handle) {
      if (!handle) return '#';
      return `/products/${handle}`;
    }

    replaceSelectOptions(select, items) {
      if (!select) return;

      select.innerHTML = '';

      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.textContent = this.selectPlaceholder;
      select.appendChild(defaultOption);

      items.forEach((item) => {
        const option = document.createElement('option');
        option.value = item.value;
        option.textContent = item.label;
        select.appendChild(option);
      });
    }

    resetCars() {
      this.replaceSelectOptions(this.carSelect, []);
      this.carSelect.disabled = true;
      this.carSelect.value = '';
    }

    resetYears() {
      this.replaceSelectOptions(this.yearSelect, []);
      this.yearSelect.disabled = true;
      this.yearSelect.value = '';
    }

    updateSubmitState() {
      if (!this.submitButton) return;
      this.submitButton.disabled = !this.currentSpecId;
    }

    resetAll() {
      this.currentMakerId = '';
      this.currentCarId = '';
      this.currentSpecId = '';

      if (this.makerSelect) this.makerSelect.value = '';
      this.resetCars();
      this.resetYears();
      this.updateSubmitState();
      this.hideResults();
      this.hideError();
      this.hideEmpty();
    }

    clearAllGroups() {
      Object.values(this.groups).forEach((group) => {
        if (!group) return;
        if (group.container) group.container.innerHTML = '';
        if (group.wrapper) group.wrapper.hidden = true;
      });
    }

    hideResults() {
      this.clearAllGroups();
      if (this.resultsSummary) this.resultsSummary.textContent = '';
      if (this.resultsWrap) this.resultsWrap.hidden = true;
    }

    showEmpty() {
      this.hideResults();
      this.hideError();

      if (this.emptyState) {
        this.emptyState.hidden = false;
      }

      if (this.resultsWrap) {
        this.resultsWrap.hidden = false;
      }
    }

    hideEmpty() {
      if (this.emptyState) {
        this.emptyState.hidden = true;
      }
    }

    showError() {
      this.hideResults();
      this.hideEmpty();

      if (this.errorState) {
        this.errorState.hidden = false;
      }

      if (this.resultsWrap) {
        this.resultsWrap.hidden = false;
      }
    }

    hideError() {
      if (this.errorState) {
        this.errorState.hidden = true;
      }
    }

    destroy() {
      this.isDestroyed = true;

      this.cleanupFns.forEach((cleanup) => cleanup());
      this.cleanupFns.length = 0;

      this.prefetchObserver?.disconnect();
      this.prefetchObserver = null;
    }
  }

  const initSection = (section) => {
    if (!section || controllers.has(section)) return;

    const controller = new ChildseatFitChecker(section);
    controller.init();
    controllers.set(section, controller);
  };

  const destroySection = (section) => {
    const controller = controllers.get(section);
    if (!controller) return;

    controller.destroy();
    controllers.delete(section);
  };

  const initAll = (scope = document) => {
    querySections(scope).forEach(initSection);
  };

  const destroyAll = (scope = document) => {
    querySections(scope).forEach(destroySection);
  };

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));

  function selectedSpecNoteArraySafe(data, specId) {
    const spec = (data?.specs || []).find((item) => item.id === specId);
    return Array.isArray(spec?.notes) ? spec.notes : [];
  }
})();
